import { useState, useMemo } from 'react';
import { useGT } from 'gt-react';
import useOrganizationMembers from 'src/api/organization/useOrganizationMembers';
import { Combobox } from '@/components/ui/combobox';
import { pickerLabels } from '@/components/common/inputs/pickerLabels';
import type { UserPickerProps } from './userPickerProps';

interface UserOption {
  userId: string;
  name: string;
  email: string;
}

export function UserAutocomplete({
  userId,
  onSelect,
  className,
  disabled,
  excludeUserIds,
  placeholder,
}: UserPickerProps) {
  const gt = useGT();
  const [search, setSearch] = useState('');
  const { data: membersData, isLoading } = useOrganizationMembers(true);

  const options: UserOption[] = useMemo(() => {
    const members = membersData?.members ?? [];
    const excluded = new Set(excludeUserIds ?? []);
    return members
      .filter((m) => m.deactivatedAt === null && !excluded.has(m.userId))
      .map((m) => ({ userId: m.userId, name: m.name, email: m.email }));
  }, [membersData, excludeUserIds]);

  return (
    <Combobox
      items={options}
      value={userId || null}
      onValueChange={(next) => onSelect(next ?? '')}
      deselectable
      getValue={(user) => user.userId}
      getLabel={(user) => `${user.name} (${user.email})`}
      search={search}
      onSearchChange={setSearch}
      resetSearchOnClose={false}
      disabled={disabled || isLoading}
      className={className}
      contentClassName="w-full"
      labels={pickerLabels({
        placeholder:
          placeholder ??
          gt('Search users...', { context: 'user autocomplete placeholder' }),
        search: gt('Search options...', {
          context: 'search box placeholder inside a picker dropdown',
        }),
      })}
    />
  );
}
